import React, { useCallback, useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import { hot } from 'react-hot-loader/root'
import useTask from 'actionHub/hooks/useTask'
import Loading from 'components/Loading'
import CaseDescription from 'components/CaseDescription'
import { jobPermissions } from 'utils/permissions'
import DetailedViewHeader from 'components/DetailedViewHeader'
import DataList from 'actionHub/views/DataList/DataList'
import ReviewSection from 'actionHub/components/ReviewSection'
import DialogSimple from 'components/DialogSimple'
import WarningIcon from '@material-ui/icons/Warning'
import { Heading } from '@deloitte/gel-library'
import DataPageWrapper from 'actionHub/views/DataPageWrapper/DataPageWrapper'
import EditDataDialog from 'actionHub/views/EditDataDialog/EditDataDialog'
import { useActionPropTypes } from 'actionHub/hooks/useAction'
import { useJobPropTypes } from 'hooks/useJob'
import EditDetailsForm from 'actionHub/views/EditDetailsForm'
import { useDispatch, useSelector } from 'react-redux'
import { modalCreate, modalShow } from 'actions/modals'
import constants from 'constants'
import taskStyles from './Task.scss'

function Task({
  history, useJob, useAction, match: { params: { actionSetId, taskId } },
}) {
  const dispatch = useDispatch()
  const [isDialogVisible, setIsDialogVisible] = useState(false)
  const [editingDataId, setEditingDataId] = useState(null)
  const [expanded, setExpanded] = useState(false)
  const [showConfirmBackDialog, setShowConfirmBackDialog] = useState(false)
  const modals = useSelector(state => state.modals)
  const { packageName } = useSelector(state => state.job)
  const MODAL_ID = constants.MODAL_EDIT_TASK_DETAILS

  const {
    data, columns, taskData, meta, taskTitle, setLabelValue, isFetched, fetchNextPage,
    submitTaskLabels, validateLabel, setTasksActionState, setTaskAssignee, setTaskPriority,
    allowComments, detailColumns, updateTaskDetails, saveFindings, resetLabelsToDefault,
  } = useTask(actionSetId, taskId)

  const {
    isCurrent, priorities, columns: taskColumns, setUpdatedTag, deleteTagAssociation, allowTags, actionStates,
  } = useAction

  useEffect(() => {
    if (modals && !modals.modalNames.includes(MODAL_ID)) {
      dispatch(modalCreate(MODAL_ID))
    }
  }, [modals, MODAL_ID, dispatch])

  const onEditDetailsClick = useCallback(() => {
    dispatch(modalShow(MODAL_ID))
  }, [MODAL_ID, dispatch])

  const handleDataClick = useCallback((dataItem) => {
    setEditingDataId(dataItem._id)
    setIsDialogVisible(true)
  }, [])

  const handleDialogDismiss = useCallback(() => {
    setEditingDataId(null)
    setIsDialogVisible(false)
  }, [])

  const handleBackToActionSet = () => {
    setShowConfirmBackDialog(false)
    history.push(`/action/${actionSetId}`)
  }

  const handleConfirmSave = () => {
    saveFindings(false)
    handleBackToActionSet()
  }

  const handleDiscardChanges = () => {
    resetLabelsToDefault(taskId)
    handleBackToActionSet()
  }

  if (!data || !data._isFetched || !isFetched) {
    return <Loading pageLoading />
  }

  const {
    deloitteTeam, externalTeam, userHasJobPermission, job: { jobId, hasExpired }, isAssignedActionsEditor,
  } = useJob

  const updatedUser = [...deloitteTeam, ...externalTeam]
    .find(m => m.userDetails.email === data.updatedBy)

  const canEditActions = userHasJobPermission(jobPermissions.actionHubEdit)
  const disabled = !isCurrent || !canEditActions || hasExpired

  const isControlsAdvantage = packageName.toLowerCase() === 'ControlsAdvantage'.toLowerCase()

  return (
    <div className={taskStyles.base}>
      <div className={taskStyles.caseHeader}>
        <DetailedViewHeader
          actionSetId={actionSetId}
          actionStates={actionStates}
          allowTags={allowTags}
          clients={externalTeam}
          deleteTagAssociation={deleteTagAssociation}
          disabled={disabled}
          handleBackToActionSet={handleBackToActionSet}
          headerType="caseView"
          hideDropdowns={isAssignedActionsEditor}
          members={deloitteTeam}
          priorities={priorities}
          setShowConfirmBackDialog={setShowConfirmBackDialog}
          setTaskAssignee={setTaskAssignee}
          setTaskPriority={setTaskPriority}
          setTaskTag={setUpdatedTag}
          setTasksActionState={setTasksActionState}
          task={data}
          taskTitle={taskTitle}
          timestamp={data.updatedDate === null ? '' : data.updatedDate}
          updatedUser={updatedUser}
        />
      </div>

      <DataPageWrapper
        expanded={expanded}
        hasChildData={data._hasChildData}
      >
        <div className={taskStyles.leftContainer}>
          <CaseDescription
            isFullHeight
            columns={detailColumns || taskColumns}
            onEditDetailsClick={onEditDetailsClick}
            showEditDetailsButton={isControlsAdvantage && !isAssignedActionsEditor}
            task={data}
          />
        </div>
        <div className={taskStyles.rightContainer}>
          <ReviewSection
            actionSetId={actionSetId}
            allowComments={allowComments}
            comments={data.comments}
            disabled={disabled}
            hasExpired={hasExpired}
            isAssignedActionsEditor={isAssignedActionsEditor}
            jobId={jobId}
            object={data}
            setLabelValue={setLabelValue}
            submitLabels={submitTaskLabels}
            taskId={taskId}
            validateLabel={validateLabel}
          />
        </div>
      </DataPageWrapper>

      {detailColumns && modals.modalNames.includes(MODAL_ID) && (
        <EditDetailsForm
          columns={detailColumns}
          isNewForm={false}
          modalId={MODAL_ID}
          onSaveClick={updateTaskDetails}
          task={data}
        />
      )}

      {isFetched && data._hasChildData && (
        <>
          <DataList
            columns={columns}
            data={taskData}
            // dataCount={dataCount}
            expanded={expanded}
            meta={meta}
            onClick={handleDataClick}
            onLoadMore={fetchNextPage}
            setExpanded={setExpanded}
          />
          <EditDataDialog
            actionSetId={actionSetId}
            disabled={!isCurrent || !canEditActions}
            editingDataId={editingDataId}
            isOpen={isDialogVisible}
            members={deloitteTeam}
            onDismiss={handleDialogDismiss}
            taskId={taskId}
          />
        </>
      )}

      <DialogSimple
        actionLabel="Save findings"
        className={taskStyles.confirmDialog}
        discardLabel="Discard"
        dismissLabel="Cancel"
        isOpen={showConfirmBackDialog}
        onAction={handleConfirmSave}
        onDiscard={handleDiscardChanges}
        onDismiss={() => setShowConfirmBackDialog(false)}
      >
        <div className={taskStyles.confirmContainer}>
          <WarningIcon fontSize="large" />
          <div>
            <Heading className={taskStyles.heading} level={7}>Unsaved changes</Heading>
            <p>Do you want to save your changes before you leave this page?</p>
          </div>
        </div>
      </DialogSimple>
    </div>

  )
}

Task.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      actionSetId: PropTypes.string,
      taskId: PropTypes.string,
    }),
  }).isRequired,
  useAction: useActionPropTypes.isRequired,
  useJob: useJobPropTypes.isRequired,
}

export default hot(withRouter(Task))
